/* One shared page cursor, three independent visible counts. Never filter outside the current collection. */
if (!customElements.get('kr-colour-collection')) {
  customElements.define('kr-colour-collection', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.batch = Number(this.dataset.batch) || 4;
      this.chapters = [...this.querySelectorAll('[data-family]')];
      this.next = this.querySelector('[data-next]').dataset.next;
      this.fallback = this.querySelector('[data-fallback]');
      this.error = this.querySelector('[data-error]');
      this.desired = Object.fromEntries(this.chapters.map(c => [c.dataset.family, this.batch]));
      this.seen = new Set([...this.querySelectorAll('[data-product]')].map(c => c.dataset.product));
      this.addEventListener('load', event => this.readyImage(event.target), {capture:true, signal:this.abort.signal});
      this.prepareImages();
      if (this.fallback) this.fallback.hidden = true;
      this.chapters.forEach(chapter => chapter.querySelector('[data-more]').addEventListener('click', () => this.more(chapter)));
      this.paint();
      this.fill().catch(() => this.fail());
    }
    disconnectedCallback() { this.abort?.abort(); }
    cards(chapter) { return [...chapter.querySelectorAll('[data-product]')]; }
    readyImage(image) {
      if (image.matches?.('.kr-colour-card__image--second') && image.complete && image.naturalWidth > 0) {
        image.classList.add('kr-image-ready');
      }
    }
    prepareImages() { this.querySelectorAll('.kr-colour-card__image--second').forEach(image => this.readyImage(image)); }
    paint() {
      const empty = this.querySelector('[data-empty-available]');
      if (empty) empty.hidden = Boolean(this.next) || this.querySelectorAll('[data-product]').length > 0;
      this.chapters.forEach(chapter => {
        const cards = this.cards(chapter);
        const amount = this.desired[chapter.dataset.family];
        cards.forEach((card, index) => { card.hidden = index >= amount; });
        chapter.hidden = cards.length === 0;
        this.querySelectorAll(`[data-family-link="${chapter.dataset.family}"]`).forEach(link => { link.hidden = chapter.hidden; });
        const shown = Math.min(cards.length, amount);
        const button = chapter.querySelector('[data-more]');
        button.hidden = cards.length <= amount && !this.next;
        chapter.querySelector('[data-status]').textContent = this.next ? `${shown} rugs shown` : `${shown} of ${cards.length} rugs shown`;
      });
    }
    async fetchPage() {
      if (!this.next) return;
      if (this.pending) return this.pending;
      this.pending = (async () => {
        const url = new URL(this.next, location.origin);
        if (url.origin !== location.origin) throw new Error('Unexpected collection URL');
        url.searchParams.set('section_id', this.dataset.section);
        url.searchParams.set('view', this.dataset.view || 'shop-by-colour');
        // Retain the selected unpublished theme during preview; never change the product source.
        const theme = new URL(location.href).searchParams.get('preview_theme_id');
        if (theme) url.searchParams.set('preview_theme_id', theme);
        const response = await fetch(url, {signal:this.abort.signal});
        if (!response.ok) throw new Error('Collection request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = doc.querySelector('kr-colour-collection');
        if (!incoming) throw new Error('Collection response missing');
        const next = incoming.querySelector('[data-next]').dataset.next;
        if (next && next === this.next) throw new Error('Collection pagination did not advance');
        this.chapters.forEach(chapter => {
          incoming.querySelectorAll(`[data-family="${chapter.dataset.family}"] [data-product]`).forEach(card => {
            if (this.seen.has(card.dataset.product)) return;
            this.seen.add(card.dataset.product);
            card.hidden = true;
            chapter.querySelector('[data-family-grid], .kr-colour__grid').append(document.importNode(card, true));
          });
        });
        this.next = next;
        this.prepareImages();
        this.paint();
      })();
      try { await this.pending; } finally { this.pending = null; }
    }
    async fill(chapter) {
      const targets = chapter ? [chapter] : this.chapters;
      while (this.next && targets.some(c => this.cards(c).length < this.desired[c.dataset.family])) {
        await this.fetchPage();
      }
      this.paint();
    }
    async more(chapter) {
      const button = chapter.querySelector('[data-more]');
      if (button.disabled) return;
      const previous = this.desired[chapter.dataset.family];
      const firstNew = this.cards(chapter)[previous];
      button.disabled = true;
      chapter.setAttribute('aria-busy', 'true');
      this.error.textContent = '';
      this.desired[chapter.dataset.family] += this.batch;
      try {
        await this.fill(chapter);
        const target = firstNew || this.cards(chapter)[previous];
        // Keep keyboard focus in the newly revealed products, not on a disappearing button.
        if (target) target.querySelector('a').focus({preventScroll:true});
      } catch (error) {
        this.desired[chapter.dataset.family] = previous;
        this.paint();
        this.fail(error);
      } finally { button.disabled = false; chapter.removeAttribute('aria-busy'); }
    }
    fail(error) {
      if (error?.name === 'AbortError' || this.abort.signal.aborted) return;
      this.error.textContent = 'Some rugs could not be loaded. Please try See more again, or use the next-page link.';
      if (this.fallback) { this.fallback.hidden = false; if (this.next) this.fallback.href = this.next; }
    }
  });
}
